import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import {
  colors,
  gradients,
  shadows,
  typography,
  borderRadius,
  getCosmicBackground,
  getCardStyle,
  getButtonStyle,
  getGradientTextStyle,
  getStatusBadgeStyle,
} from '../theme';

interface User {
  id: number;
  email: string;
  subscription_status: string;
  trial_end_date?: string;
}

interface UsageStats {
  calculations_used: number;
  free_limit: number;
  remaining_calculations: number;
  subscription_status: string;
}

interface Calculation {
  id: number;
  decision_type: string;
  decision_category: string;
  created_at: string;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [calculations, setCalculations] = useState<Calculation[]>([]);
  const [hasBirthData, setHasBirthData] = useState(false);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchUserData = async () => {
    try {
      const userRes = await fetch(`${API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!userRes.ok) throw new Error('Session expired');
      const userData = await userRes.json();
      setUser(userData);

      const usageRes = await fetch(`${API_URL}/users/usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (usageRes.ok) {
        const usageData = await usageRes.json();
        setUsage(usageData);
      }

      const birthRes = await fetch(`${API_URL}/users/birth-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHasBirthData(birthRes.ok);

      const calcRes = await fetch(`${API_URL}/calculations/history?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (calcRes.ok) {
        const calcData = await calcRes.json();
        setCalculations(calcData || []);
      }
    } catch (error) {
      localStorage.removeItem('auth_token');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{
        ...getCosmicBackground(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.text.starWhite,
        fontFamily: typography.fontFamily.heading,
        fontSize: typography.fontSize.xl,
        letterSpacing: typography.letterSpacing.wider,
      }}>
        <span style={{ color: colors.accent.gold, marginRight: '12px' }}>★</span>
        Loading...
      </div>
    );
  }

  return (
    <div style={{
      ...getCosmicBackground(),
      padding: '24px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Star pattern background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 20px 30px, rgba(212, 175, 55, 0.3), transparent),
          radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.4), transparent),
          radial-gradient(2px 2px at 130px 80px, rgba(157, 143, 219, 0.2), transparent),
          radial-gradient(1px 1px at 180px 150px, rgba(255, 255, 255, 0.3), transparent),
          radial-gradient(1.5px 1.5px at 240px 100px, rgba(212, 175, 55, 0.25), transparent)
        `,
        backgroundSize: '300px 250px',
        pointerEvents: 'none',
        opacity: 0.5,
        zIndex: 0,
      }} />

      {/* Header */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto 28px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <span style={{
            fontSize: '32px',
            marginRight: '12px',
            color: colors.accent.gold,
            textShadow: `0 0 15px rgba(212, 175, 55, 0.5)`,
          }}>★</span>
          <span style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            ...getGradientTextStyle('gold'),
          }}>StarDecision</span>
        </Link>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Link to="/profile" style={{
            color: colors.text.secondary,
            textDecoration: 'none',
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            transition: 'color 0.3s ease',
          }}>Profile</Link>
          <button onClick={handleLogout} style={{
            ...getButtonStyle('ghost'),
            padding: '8px 16px',
            fontSize: typography.fontSize.sm,
          }}>
            Logout
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Welcome Section */}
        <div style={{
          ...getCardStyle('default'),
          marginBottom: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h1 style={{
                fontFamily: typography.fontFamily.heading,
                fontSize: typography.fontSize['2xl'],
                fontWeight: typography.fontWeight.bold,
                color: colors.text.starWhite,
                margin: '0 0 8px 0',
                letterSpacing: typography.letterSpacing.wide,
              }}>
                Welcome back <span style={{ color: colors.accent.gold }}>★</span>
              </h1>
              <p style={{
                color: colors.text.secondary,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
              }}>
                <span>{user?.email}</span>
                <span style={getStatusBadgeStyle(user?.subscription_status as 'premium' | 'trial' | 'free' || 'free')}>
                  {user?.subscription_status?.toUpperCase()}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Alert if no birth data */}
        {!hasBirthData && (
          <div style={{
            ...getCardStyle('gold'),
            marginBottom: '24px',
            background: colors.status.warningBg,
            border: `1px solid ${colors.status.warning}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h3 style={{
                  fontFamily: typography.fontFamily.heading,
                  fontSize: typography.fontSize.lg,
                  color: colors.status.warning,
                  margin: '0 0 6px 0',
                  letterSpacing: typography.letterSpacing.wide,
                }}>
                  ⚠ Birth Date Required
                </h3>
                <p style={{ margin: 0, color: colors.text.secondary, fontSize: typography.fontSize.sm }}>
                  Please enter your birth data to start getting personalized astrological timing guidance.
                </p>
              </div>
              <Link to="/birth-data" style={{ textDecoration: 'none' }}>
                <button style={{
                  ...getButtonStyle('gold'),
                  padding: '12px 24px',
                }}>
                  Enter Birth Date
                </button>
              </Link>
            </div>
          </div>
        )}

        {/* Main Grid */}
        <div className="dashboard-grid">
          {/* Quick Actions */}
          <div style={getCardStyle('default')}>
            <h2 style={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.starWhite,
              margin: '0 0 20px 0',
              letterSpacing: typography.letterSpacing.wide,
            }}>Quick Actions</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link to="/calculate" style={{
                textDecoration: 'none',
                pointerEvents: hasBirthData ? 'auto' : 'none',
              }}>
                <button style={{
                  ...getButtonStyle('gold'),
                  width: '100%',
                  opacity: hasBirthData ? 1 : 0.5,
                  justifyContent: 'center',
                }}>
                  ◈ New Calculation
                </button>
              </Link>
              <Link to="/birth-data" style={{ textDecoration: 'none' }}>
                <button style={{
                  ...getButtonStyle('ghost'),
                  width: '100%',
                  justifyContent: 'center',
                }}>
                  ☼ {hasBirthData ? 'View' : 'Enter'} Birth Date
                </button>
              </Link>
              {user?.subscription_status === 'free' && (
                <Link to="/subscription" style={{ textDecoration: 'none' }}>
                  <button style={{
                    ...getButtonStyle('outline'),
                    width: '100%',
                    justifyContent: 'center',
                  }}>
                    ★ Upgrade to Premium
                  </button>
                </Link>
              )}
            </div>
          </div>

          {/* Usage Stats */}
          <div style={getCardStyle('default')}>
            <h2 style={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.starWhite,
              margin: '0 0 20px 0',
              letterSpacing: typography.letterSpacing.wide,
            }}>This Month's Usage</h2>
            {usage && (() => {
              // Detect if user has unlimited access (premium or trial)
              const hasUnlimited = usage.subscription_status === 'premium' ||
                usage.subscription_status === 'trial' ||
                user?.subscription_status === 'premium' ||
                user?.subscription_status === 'trial' ||
                (user?.trial_end_date && new Date(user.trial_end_date) > new Date());

              return (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ color: colors.text.secondary, fontSize: typography.fontSize.sm }}>Calculations Used</span>
                    <span style={{
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text.starWhite,
                    }}>
                      {usage.calculations_used} / {hasUnlimited ? '\u221E' : usage.free_limit}
                    </span>
                  </div>
                  {!hasUnlimited && (
                    <div style={{
                      background: 'rgba(74, 63, 138, 0.3)',
                      borderRadius: borderRadius.full,
                      height: '10px',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        background: gradients.goldButton,
                        height: '100%',
                        width: `${Math.min((usage.calculations_used / usage.free_limit) * 100, 100)}%`,
                        borderRadius: borderRadius.full,
                        transition: 'width 0.5s ease',
                        boxShadow: shadows.goldGlow,
                      }} />
                    </div>
                  )}
                </div>
                {!hasUnlimited && usage.remaining_calculations <= 1 && (
                  <p style={{
                    color: colors.status.warning,
                    fontSize: typography.fontSize.sm,
                    margin: '12px 0 0 0',
                  }}>
                    ⚠ Running low on calculations! Upgrade to premium for unlimited access.
                  </p>
                )}
                {hasUnlimited && (
                  <p style={{
                    color: colors.accent.gold,
                    fontSize: typography.fontSize.sm,
                    margin: 0,
                  }}>
                    ★ Unlimited calculations with your premium access!
                  </p>
                )}
              </div>
              );
            })()}
          </div>

          {/* Recent Calculations */}
          <div className="dashboard-full-width" style={getCardStyle('default')}>
            <h2 style={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.starWhite,
              margin: '0 0 20px 0',
              letterSpacing: typography.letterSpacing.wide,
            }}>Recent Calculations</h2>
            {calculations.length === 0 ? (
              <p style={{
                color: colors.text.muted,
                textAlign: 'center',
                padding: '40px 0',
                fontSize: typography.fontSize.base,
              }}>
                No calculations yet. Start by creating your first calculation!
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {calculations.map((calc) => (
                  <Link
                    key={calc.id}
                    to={`/results/${calc.id}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      background: 'rgba(74, 63, 138, 0.15)',
                      borderRadius: borderRadius.md,
                      textDecoration: 'none',
                      color: 'inherit',
                      transition: 'all 0.3s ease',
                      border: `1px solid ${colors.border.subtle}`,
                    }}
                  >
                    <div>
                      <div style={{
                        fontWeight: typography.fontWeight.semibold,
                        color: colors.text.starWhite,
                        textTransform: 'capitalize',
                        marginBottom: '4px',
                      }}>
                        {calc.decision_type.replace('_', ' ')}
                      </div>
                      <div style={{
                        fontSize: typography.fontSize.xs,
                        color: colors.text.muted,
                      }}>
                        {calc.decision_category} • {new Date(calc.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span style={{
                      color: colors.accent.gold,
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.medium,
                    }}>View →</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
